import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Zap, ShieldCheck, Truck, Sparkles, Phone, ArrowRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) navigate("/profile", { replace: true });
  }, [user, loading, navigate]);

  const [redirecting, setRedirecting] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = async () => {
    setRedirecting(true);
    try {
      const redirect_uri = window.location.origin + "/auth/google";
      const { data } = await api.get("/auth/google/url", { params: { redirect_uri } });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start Google sign-in");
      setRedirecting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-12" data-testid="login-page">
      <div className="grid lg:grid-cols-2 gap-10 max-w-5xl w-full items-center">
        <div className="hidden lg:block">
          <p className="vm-overline">Welcome to Quiktronic</p>
          <h1 className="font-display text-5xl font-bold mt-2 leading-[0.95] tracking-tighter">
            Sign in.<br />
            Shop at <span className="text-volt-400">light-speed</span>.
          </h1>
          <p className="text-neutral-400 mt-4 leading-relaxed max-w-md">
            One Quiktronic account for shopping, repair bookings, and your buyback dashboard, all in one place.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-8">
            {[
              { icon: Zap, t: "30-min delivery" },
              { icon: ShieldCheck, t: "100% authentic" },
              { icon: Truck, t: "Free pickup" },
              { icon: Sparkles, t: "Best prices" },
            ].map((b, i) => (
              <div key={i} className="vm-card p-4 flex items-center gap-3">
                <b.icon className="h-5 w-5 text-volt-400" />
                <span className="text-sm font-semibold">{b.t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="vm-card p-8 lg:p-10">
          <div className="h-12 w-12 rounded-2xl bg-volt-500 flex items-center justify-center mx-auto shadow-[0_0_28px_rgba(0,85,255,0.45)]">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <h2 className="font-display text-2xl font-bold text-center mt-5">Sign in to continue</h2>
          <p className="text-sm text-neutral-400 text-center mt-2">Use your Google account to sign in securely.</p>

          <button
            onClick={handleGoogle}
            disabled={redirecting}
            className="mt-7 w-full bg-white text-black font-semibold rounded-full py-3 px-5 flex items-center justify-center gap-3 border border-neutral-300 hover:bg-neutral-100 transition-colors"
            data-testid="google-signin-btn"
          >
            <svg className="h-5 w-5" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.4-5.8 7.5-11.3 7.5-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.9 5.4 29.8 3.5 24 3.5 12.7 3.5 3.5 12.7 3.5 24S12.7 44.5 24 44.5 44.5 35.3 44.5 24c0-1.2-.1-2.3-.3-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8c1.8-3.5 5.4-6 9.5-6 3 0 5.8 1.1 7.9 3l5.7-5.7C32.5 7.4 28.4 5.5 24 5.5c-7 0-13 4-16 9.7z"/>
              <path fill="#4CAF50" d="M24 44.5c5.7 0 10.7-1.9 14.5-5.2l-6.7-5.7c-2 1.4-4.6 2.4-7.8 2.4-5.5 0-10.1-3.7-11.7-8.7l-6.7 5.2C9.6 39.6 16.2 44.5 24 44.5z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.3-4.5 5.7l6.7 5.7C39.4 36.7 44.5 31.1 44.5 24c0-1.2-.1-2.3-.3-3.5z"/>
            </svg>
            {redirecting ? "Opening Google…" : "Continue with Google"}
          </button>

          <p className="text-xs text-neutral-500 mt-6 text-center">
            By continuing, you agree to our{" "}
            <Link to="/terms" className="underline hover:text-volt-400" data-testid="login-terms-link">Terms</Link> &{" "}
            <Link to="/privacy" className="underline hover:text-volt-400" data-testid="login-privacy-link">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

// Phone OTP block: currently unused (phone login disabled until a real SMS provider is connected)
export function PhoneOtpBlock({ linkMode = false, onSuccess }) {
  const [phase, setPhase] = useState("enter"); // enter | verify
  const [phone, setPhone] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const requestOtp = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/phone/send", { phone });
      setNormalizedPhone(data.phone);
      setCooldown(data.cooldown_seconds || 30);
      setDevOtp(data.dev_otp || null);
      setOtp(Array(6).fill(""));
      setPhase("verify");
      toast.success(data.dev_otp ? `Dev OTP: ${data.dev_otp}` : `OTP sent to ${data.phone}`);
      setTimeout(() => inputRefs.current[0]?.focus(), 40);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send OTP");
    } finally {
      setSending(false);
    }
  };

  const onOtpChange = (i, v) => {
    const digit = v.replace(/\D/g, "").slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[i] = digit;
      return next;
    });
    if (digit && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const onOtpKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const onOtpPaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const arr = Array(6).fill("");
    for (let i = 0; i < pasted.length; i++) arr[i] = pasted[i];
    setOtp(arr);
    const lastIdx = Math.min(pasted.length, 5);
    inputRefs.current[lastIdx]?.focus();
  };

  const verifyOtp = async () => {
    const code = otp.join("");
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/phone/verify", {
        phone: normalizedPhone,
        otp: code,
        link_existing: linkMode,
      });
      toast.success(linkMode ? "Phone linked" : "Signed in");
      onSuccess?.(data.user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify OTP");
    } finally {
      setVerifying(false);
    }
  };

  if (phase === "enter") {
    return (
      <div data-testid="phone-otp-block">
        <label className="text-xs font-semibold text-neutral-400">Mobile number</label>
        <div className="mt-2 flex items-center gap-2 bg-ink-800/50 border border-white/10 rounded-full px-4 py-2.5 focus-within:border-volt-500">
          <Phone className="h-4 w-4 text-neutral-500" />
          <span className="text-sm font-semibold text-neutral-200">+91</span>
          <span className="text-neutral-700">|</span>
          <input
            inputMode="tel"
            autoComplete="tel"
            placeholder="10-digit number"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            onKeyDown={(e) => { if (e.key === "Enter" && phone.length >= 10) requestOtp(); }}
            className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-neutral-500"
            data-testid="phone-input"
          />
        </div>
        <button
          onClick={requestOtp}
          disabled={sending || phone.length < 10}
          className="mt-4 w-full bg-volt-500 hover:bg-volt-600 disabled:opacity-40 text-white font-semibold rounded-full py-3 px-5 flex items-center justify-center gap-2 transition-colors"
          data-testid="phone-send-otp-btn"
        >
          {sending ? "Sending..." : <>Send OTP <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="phone-otp-verify">
      <p className="text-sm text-neutral-300">
        Enter the 6-digit code sent to <span className="font-semibold">{normalizedPhone}</span>
      </p>
      {devOtp && (
        <p className="mt-1 text-[11px] text-volt-400 font-mono" data-testid="phone-dev-otp">
          Dev OTP: {devOtp}
        </p>
      )}
      <div className="mt-4 flex gap-2 justify-between" onPaste={onOtpPaste}>
        {otp.map((v, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={v}
            onChange={(e) => onOtpChange(i, e.target.value)}
            onKeyDown={(e) => onOtpKey(i, e)}
            className="h-12 w-11 text-center font-display text-xl font-bold bg-ink-800/50 border border-white/10 rounded-xl focus:border-volt-500 focus:outline-none text-white"
            data-testid={`otp-input-${i}`}
          />
        ))}
      </div>
      <button
        onClick={verifyOtp}
        disabled={verifying || otp.join("").length !== 6}
        className="mt-4 w-full bg-volt-500 hover:bg-volt-600 disabled:opacity-40 text-white font-semibold rounded-full py-3 px-5 transition-colors"
        data-testid="phone-verify-btn"
      >
        {verifying ? "Verifying..." : (linkMode ? "Link phone" : "Verify & continue")}
      </button>
      <div className="mt-3 flex items-center justify-between text-xs">
        <button
          onClick={() => setPhase("enter")}
          className="text-neutral-400 hover:text-white"
          data-testid="phone-change-btn"
        >
          Change number
        </button>
        <button
          onClick={requestOtp}
          disabled={cooldown > 0 || sending}
          className={cooldown > 0 ? "text-neutral-500" : "text-volt-400 hover:text-volt-300 font-semibold"}
          data-testid="phone-resend-btn"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
        </button>
      </div>
    </div>
  );
}
